import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  DialogActions,
  DialogContent,
  Grid,
  Typography,
  makeStyles,
  Tooltip,
} from '@material-ui/core'

import { ChevronDown, Copy, ArrowRight } from 'react-feather'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { theme } from '../../../theme'
import { validators } from '../../../validation/validators'
import { Dialog } from '../../dialog/Dialog'
import { ControlledTextField } from '../../form/ControlledTextField'
import { FormGroup } from '../../form/FormGroup'
import type { FormValues } from './form-values'
import { ControlledToggleButtonGroup } from '../../form/ControlledToggleButtonGroup'
import { DayProfileType } from '../../../generated/graphql'
import { ControlledDatePicker } from '../../form/ControlledDatePicker'
import {
  addDaysToDate,
  getMaxDate,
  getMinDate,
} from '../../common/util/date-util'

type Props = {
  open: boolean
  onSubmit: (values: FormValues) => void
  defaultValues?: Partial<FormValues>
  onClose: () => void
  isCreate: boolean
  onDelete?: () => void
}

const useStyles = makeStyles({
  iconbutton: {
    zIndex: 1,
    padding: 'unset',
  },
})

export function ScheduleDialog({
  open,
  onSubmit,
  onClose,
  defaultValues,
  isCreate,
  onDelete,
}: Props): React.ReactElement {
  const { t } = useTranslation()
  const { control, handleSubmit, errors, setValue, watch } =
    useForm<FormValues>({
      defaultValues,
      mode: 'onBlur',
    })
  const classes = useStyles()

  const title = isCreate
    ? t('buildingEnergyModels.drawer.addSchedule')
    : defaultValues?.name ?? ''

  const normalValues = watch('hourlyNormals')

  const handleCopyValues = () => {
    if (normalValues)
      for (let i = 0; i < normalValues.length; i++) {
        setValue(`hourlyExceptions.${i}`, normalValues[i])
      }
  }

  const exceptionUse = watch('exceptionInUse')
  const exceptionStart = watch('exceptionStart')
  const exceptionEnd = watch('exceptionEnd')

  return (
    <Dialog title={title} open={open} onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <FormGroup>
            <ControlledTextField
              control={control}
              name="name"
              error={errors.name}
              label={t('schedule.name')}
              rules={validators.required}
              type="name"
            />
          </FormGroup>

          <Accordion elevation={3}>
            <AccordionSummary
              expandIcon={
                <ChevronDown id="collection-schedule-expand-button" />
              }
            >
              <Box display="flex" alignItems="center">
                <Typography>{t('schedule.hourlyProfile')}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <FormGroup>
                <Grid container spacing={1}>
                  <Grid
                    container
                    key="hourheader"
                    item
                    alignItems="center"
                    xs={12}
                    spacing={3}
                  >
                    <Grid item xs={3}>
                      <Typography>{t('common.hour')}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography>{t('schedule.normal')}</Typography>
                    </Grid>
                    <Grid item xs={1} style={{ paddingLeft: '0px' }}>
                      <Tooltip title={`${t('schedule.copyValuesTooltip')}`}>
                        <IconButton
                          color="primary"
                          className={classes.iconbutton}
                          onClick={handleCopyValues}
                        >
                          <Copy size={20} />
                          <ArrowRight size={20} />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography>{t('schedule.exception')}</Typography>
                    </Grid>
                  </Grid>

                  {Array.from(Array(24)).map((_, index) => (
                    <Grid
                      container
                      key={`hourlyRow-${index.toString()}`}
                      item
                      alignItems="center"
                      xs={12}
                      spacing={3}
                    >
                      <Grid item xs={3}>
                        <Typography>{index}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <ControlledTextField
                          key={`normal-${index.toString()}`}
                          control={control}
                          name={`hourlyNormals.${index}`}
                          error={errors.hourlyNormals?.at(index)}
                          rules={{
                            ...validators.required,
                            ...validators.copModifier,
                          }}
                          defaultValue="0"
                          validateOnBlur
                          InputProps={{ margin: 'dense' }}
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <Typography />
                      </Grid>
                      <Grid item xs={4}>
                        <ControlledTextField
                          key={`exception-${index.toString()}`}
                          control={control}
                          name={`hourlyExceptions.${index}`}
                          error={errors.hourlyExceptions?.at(index)}
                          rules={{
                            ...validators.required,
                            ...validators.copModifier,
                          }}
                          defaultValue="0"
                          validateOnBlur
                          InputProps={{ margin: 'dense' }}
                        />
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>
            </AccordionDetails>
          </Accordion>

          <Accordion style={{ marginTop: theme.spacing(1) }} elevation={3}>
            <AccordionSummary
              expandIcon={
                <ChevronDown id="collection-schedule-expand-button" />
              }
            >
              <Box display="flex" alignItems="center">
                <Typography>{t('schedule.weeklyProfile')}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <FormGroup>
                <Grid container spacing={1}>
                  <Grid
                    container
                    key="weekheader"
                    item
                    alignItems="center"
                    xs={12}
                    spacing={3}
                  >
                    <Grid item xs={3}>
                      <Typography>{t('schedule.weekday')}</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <Typography>{t('schedule.dayProfile')}</Typography>
                    </Grid>
                  </Grid>

                  {Array.from(Array(7)).map((_, index) => (
                    <Grid
                      container
                      key={`weeklyRow-${index.toString()}`}
                      item
                      alignItems="center"
                      xs={12}
                      spacing={3}
                    >
                      <Grid item xs={3}>
                        <Typography>
                          {t(`date.weekdays.${index + 1}`)}
                        </Typography>
                      </Grid>
                      <Grid item xs={8}>
                        <ControlledToggleButtonGroup
                          key={`weekly-${index.toString()}`}
                          control={control}
                          name={`weekdayValues.${index}`}
                          options={[
                            {
                              value: DayProfileType.Always,
                              label: `${t('schedule.always')}`,
                            },
                            {
                              value: DayProfileType.Normal,
                              label: `${t('schedule.normal')}`,
                            },
                            {
                              value: DayProfileType.Exception,
                              label: `${t('schedule.exception')}`,
                            },
                            {
                              value: DayProfileType.Off,
                              label: `${t('schedule.off')}`,
                            },
                          ]}
                          enforceValueSet
                          exclusive
                        />
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>
            </AccordionDetails>
          </Accordion>

          <Accordion style={{ marginTop: theme.spacing(1) }} elevation={3}>
            <AccordionSummary
              expandIcon={
                <ChevronDown id="collection-schedule-expand-button" />
              }
            >
              <Box display="flex" alignItems="center">
                <Typography>{t('schedule.monthlyProfile')}</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <FormGroup>
                <Grid container spacing={1}>
                  <Grid
                    container
                    key="monthheader"
                    item
                    alignItems="center"
                    xs={12}
                    spacing={3}
                  >
                    <Grid item xs={3}>
                      <Typography>{t('common.month')}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography>{t('schedule.monthMultiplier')}</Typography>
                    </Grid>
                  </Grid>

                  {Array.from(Array(12)).map((_, index) => (
                    <Grid
                      container
                      key={`monthlyRow-${index.toString()}`}
                      item
                      alignItems="center"
                      xs={12}
                      spacing={3}
                    >
                      <Grid item xs={3}>
                        <Typography>{t(`date.months.${index + 1}`)}</Typography>
                      </Grid>
                      <Grid item xs={4}>
                        <ControlledTextField
                          key={`month-${index.toString()}`}
                          control={control}
                          name={`monthlyValues.${index}`}
                          error={errors.monthlyValues?.at(index)}
                          rules={{
                            ...validators.required,
                            ...validators.copModifier,
                          }}
                          defaultValue="0"
                          validateOnBlur
                          InputProps={{ margin: 'dense' }}
                        />
                      </Grid>
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>
            </AccordionDetails>
          </Accordion>

          <Accordion style={{ marginTop: theme.spacing(1) }} elevation={3}>
            <AccordionSummary
              expandIcon={
                <ChevronDown id="collection-schedule-expand-button" />
              }
            >
              <Box display="flex" alignItems="center">
                <Typography>
                  {t('schedule.additionalExceptionalTimetable')}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <FormGroup>
                <Grid container spacing={1}>
                  <Grid
                    container
                    key="exception"
                    item
                    alignItems="center"
                    xs={12}
                    spacing={1}
                  >
                    <Grid item xs={4}>
                      <Typography>
                        {t('schedule.additionalExceptionalTimetable')}
                      </Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <ControlledToggleButtonGroup
                        key="exceptionUse"
                        control={control}
                        name="exceptionInUse"
                        options={[
                          {
                            value: true,
                            label: `${t('schedule.exceptionInUse')}`,
                          },
                          {
                            value: false,
                            label: `${t('schedule.exceptionNotInUse')}`,
                          },
                        ]}
                        enforceValueSet
                        exclusive
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Typography>{t('schedule.exceptionStart')}</Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <ControlledDatePicker
                        control={control}
                        name="exceptionStart"
                        label=""
                        clearable
                        disabled={!exceptionUse}
                        InputProps={{ margin: 'dense' }}
                        maxDate={
                          exceptionEnd
                            ? addDaysToDate(exceptionEnd, -1)
                            : getMaxDate()
                        }
                        required
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Typography>{t('schedule.exceptionEnd')}</Typography>
                    </Grid>
                    <Grid item xs={5}>
                      <ControlledDatePicker
                        control={control}
                        name="exceptionEnd"
                        label=""
                        clearable
                        disabled={!exceptionUse}
                        InputProps={{ margin: 'dense' }}
                        minDate={
                          exceptionStart
                            ? addDaysToDate(exceptionStart, 1)
                            : getMinDate()
                        }
                        required={exceptionUse}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <Typography>{t('schedule.exceptionProfile')}</Typography>
                    </Grid>
                    <Grid item xs={8}>
                      <ControlledToggleButtonGroup
                        key="exceptionProfile"
                        control={control}
                        name="exceptionProfile"
                        options={[
                          {
                            value: DayProfileType.Always,
                            label: `${t('schedule.always')}`,
                          },
                          {
                            value: DayProfileType.Normal,
                            label: `${t('schedule.normal')}`,
                          },
                          {
                            value: DayProfileType.Exception,
                            label: `${t('schedule.exception')}`,
                          },
                          {
                            value: DayProfileType.Off,
                            label: `${t('schedule.off')}`,
                          },
                        ]}
                        enforceValueSet
                        exclusive
                        disabled={!exceptionUse}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </FormGroup>
            </AccordionDetails>
          </Accordion>
        </DialogContent>
        <DialogActions>
          <Box display="flex" flexGrow={1} gridGap={theme.spacing(1)}>
            <Box>
              {onDelete && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={onDelete}
                >
                  {t('common.remove')}
                </Button>
              )}
            </Box>
            <Box flexGrow={1} />
            <Button variant="contained" onClick={onClose}>
              {t('common.cancel')}
            </Button>

            <Button type="submit" variant="contained" color="primary">
              {isCreate ? t('common.create') : t('common.saveChanges')}
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  )
}
